// features/pos/components/PaymentModal.tsx
// ENHANCED UI/UX - Kept original functionality, improved visual design
// FIXED: Automatically add remaining balance as cash to prevent payment mismatch

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePosStore, PaymentItem } from '@/store/posStore';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/Button';
import {
    X, Banknote, CreditCard, Smartphone, Building2,
    CheckCircle, Trash2, AlertCircle, Wallet,
    Receipt, Plus, Loader2, ShieldAlert, Calculator,
    ArrowRight, RotateCcw, ChevronDown, Star, Sparkles
} from 'lucide-react';
import { clsx } from 'clsx';
import { getBankAccountsForBranch, getPaymentMethods } from '../services/posApi';
import apiClient from '@/lib/api-client';

interface PaymentModalProps {
    isOpen: boolean;
    onClose: () => void;
    onComplete: (payments: PaymentItem[], discountTotal: string) => void;
    isLoading: boolean;
    terminalDefaultBankAccount?: string;
    branchId?: string;
}

const METHOD_ICONS: Record<string, React.ElementType> = {
    cash: Banknote, card: CreditCard, mobile_money: Smartphone,
    bank_transfer: Building2, cheque: Receipt, other: Wallet,
};

const METHOD_COLORS: Record<string, { color: string; bg: string; border: string; gradient: string }> = {
    cash: { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-300', gradient: 'from-emerald-500 to-teal-600' },
    card: { color: 'text-purple-600', bg: 'bg-purple-50', border: 'border-purple-300', gradient: 'from-purple-500 to-indigo-600' },
    mobile_money: { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-300', gradient: 'from-amber-500 to-orange-600' },
    bank_transfer: { color: 'text-blue-600', bg: 'bg-blue-50', border: 'border-blue-300', gradient: 'from-blue-500 to-cyan-600' },
    cheque: { color: 'text-indigo-600', bg: 'bg-indigo-50', border: 'border-indigo-300', gradient: 'from-indigo-500 to-purple-600' },
    other: { color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-300', gradient: 'from-slate-500 to-gray-600' },
};

const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000];

export function PaymentModal({ isOpen, onClose, onComplete, isLoading, terminalDefaultBankAccount, branchId }: PaymentModalProps) {
    const { grandTotal, discountTotal } = usePosStore();
    const pos = usePosStore();
    const customerInfo = pos.customerInfo;
    const tenantId = useAuthStore(s => s.user?.tenantId) || '';
    const selectedBranchId = useAuthStore(s => s.selectedBranchId) || '';
    const effectiveBranchId = branchId || selectedBranchId;

    const [payments, setPayments] = useState<PaymentItem[]>([]);
    const [activeMethod, setActiveMethod] = useState<string>('cash');
    const [amountInput, setAmountInput] = useState<string>('');
    const [referenceInput, setReferenceInput] = useState<string>('');
    const [momoProvider, setMomoProvider] = useState<string>('');
    const [bankAccounts, setBankAccounts] = useState<any[]>([]);
    const [selectedBankAccountId, setSelectedBankAccountId] = useState<string>('');
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
    const [loadingMethods, setLoadingMethods] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showQuickAmounts, setShowQuickAmounts] = useState(false);
    const [showBankDropdown, setShowBankDropdown] = useState(false);
    const [showMomoDropdown, setShowMomoDropdown] = useState(false);
    const [isRedeemingLoyalty, setIsRedeemingLoyalty] = useState(false);
    const [loyaltyMessage, setLoyaltyMessage] = useState<string | null>(null);

    const amountInputRef = useRef<HTMLInputElement>(null);

    const safeTotal = typeof grandTotal === 'number' && !isNaN(grandTotal) ? grandTotal : 0;
    const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
    const remaining = Math.max(0, safeTotal - totalPaid);
    const isFullyPaid = remaining <= 0.01;
    const isOverpaid = totalPaid > safeTotal + 0.01;
    const paymentProgress = safeTotal > 0 ? (totalPaid / safeTotal) * 100 : 0;

    const availablePoints = Math.floor(customerInfo?.loyaltyPoints || 0);
    const availableNairaValue = Math.floor(availablePoints / 100);

    const handleRedeemLoyalty = async () => {
        if (!pos.customerId || availablePoints < 100 || remaining <= 0) return;
        setIsRedeemingLoyalty(true);
        setLoyaltyMessage(null);
        try {
            const maxNairaToUse = Math.min(availableNairaValue, remaining);
            const pointsToRedeem = Math.min(availablePoints, maxNairaToUse * 100);

            if (pointsToRedeem < 100) return;

            const { data } = await apiClient.post(`/tenants/${tenantId}/customers/${pos.customerId}/redeem-points`, {
                points: String(pointsToRedeem),
                notes: `POS Checkout Loyalty Redemption for ₦${maxNairaToUse.toFixed(2)}`,
            });

            const nairaValue = data?.data?.nairaValue ?? maxNairaToUse;
            const remainingPoints = data?.data?.remainingPoints ?? (availablePoints - pointsToRedeem);

            if (customerInfo) {
                pos.setCustomer(pos.customerId, customerInfo.name, {
                    ...customerInfo,
                    loyaltyPoints: remainingPoints,
                });
            }

            const loyaltyPayment: PaymentItem = {
                paymentMethod: 'other',
                amount: nairaValue,
                reference: `LOYALTY-${pointsToRedeem}PTS`,
            };

            const updated = [...payments, loyaltyPayment];
            setPayments(updated);
            const newTotal = updated.reduce((s, p) => s + (p.amount || 0), 0);
            const newRemaining = Math.max(0, safeTotal - newTotal);
            setAmountInput(newRemaining > 0 ? newRemaining.toFixed(2) : '0');
            setLoyaltyMessage(`Redeemed ${pointsToRedeem} points for ₦${nairaValue.toLocaleString()}!`);
        } catch (err: any) {
            setLoyaltyMessage(err?.response?.data?.error?.message || 'Failed to redeem points');
        } finally {
            setIsRedeemingLoyalty(false);
        }
    };

    const generateReference = (): string => {
        const now = new Date();
        return `TRF-${now.toISOString().slice(0, 10).replace(/-/g, '')}-${now.toTimeString().slice(0, 8).replace(/:/g, '')}`;
    };

    // Fetch payment methods
    useEffect(() => {
        if (isOpen && tenantId) {
            setLoadingMethods(true);
            getPaymentMethods(tenantId)
                .then(methods => {
                    const mapped = methods.filter(m => m.isActive).map((m, i) => ({
                        ...m,
                        icon: METHOD_ICONS[m.type] || Wallet,
                        color: METHOD_COLORS[m.type]?.color || 'text-slate-500',
                        bg: METHOD_COLORS[m.type]?.bg || 'bg-slate-50',
                        border: METHOD_COLORS[m.type]?.border || 'border-slate-300',
                        gradient: METHOD_COLORS[m.type]?.gradient || 'from-slate-400 to-gray-500',
                        key: String(i + 1),
                    }));
                    setPaymentMethods(mapped);
                })
                .catch(() => setPaymentMethods([]))
                .finally(() => setLoadingMethods(false));
        }
    }, [isOpen, tenantId]);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setPayments([]);
            setAmountInput(safeTotal.toFixed(2));
            setActiveMethod(paymentMethods.length > 0 ? paymentMethods[0].type : 'cash');
            setReferenceInput('');
            setMomoProvider('');
            setSelectedBankAccountId(terminalDefaultBankAccount || '');
            setBankAccounts([]);
            setShowConfirmModal(false);
            setShowQuickAmounts(false);
            setShowBankDropdown(false);
            setShowMomoDropdown(false);
            setLoyaltyMessage(null);
            setTimeout(() => amountInputRef.current?.focus(), 150);
        }
    }, [isOpen, safeTotal, terminalDefaultBankAccount]);

    // Fetch bank accounts
    useEffect(() => {
        if (isOpen && (activeMethod === 'bank_transfer' || activeMethod === 'card')) {
            setLoadingAccounts(true);
            getBankAccountsForBranch(tenantId, effectiveBranchId)
                .then(accounts => {
                    setBankAccounts(accounts);
                    if (terminalDefaultBankAccount && accounts.some((a: any) => a.id === terminalDefaultBankAccount)) {
                        setSelectedBankAccountId(terminalDefaultBankAccount);
                    } else if (accounts.length > 0 && (!selectedBankAccountId || !accounts.some((a: any) => a.id === selectedBankAccountId))) {
                        setSelectedBankAccountId(accounts[0].id);
                    }
                })
                .catch(() => setBankAccounts([]))
                .finally(() => setLoadingAccounts(false));
        }
    }, [isOpen, activeMethod, tenantId, effectiveBranchId, terminalDefaultBankAccount]);

    const activeMethodConfig = paymentMethods.find(m => m.type === activeMethod);
    const momoProviders: string[] = activeMethodConfig?.settings?.providers as string[] || ['Opay', 'PalmPay', 'Paga', 'MTN Momo', 'Airtel Money'];

    const formatNaira = (val: number): string => {
        if (typeof val !== 'number' || isNaN(val)) return '₦0.00';
        return `₦${val.toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
    };

    const addPayment = (amount?: number): void => {
        const amt = amount || parseFloat(amountInput);
        if (isNaN(amt) || amt <= 0) { amountInputRef.current?.focus(); return; }
        if (isFullyPaid) return;

        const actualAmount = Math.min(amt, remaining);
        const ref = activeMethod === 'bank_transfer' ? (referenceInput || generateReference()) : (referenceInput || undefined);

        const newPayment: PaymentItem = {
            paymentMethod: activeMethod as PaymentItem['paymentMethod'],
            amount: actualAmount,
            reference: ref,
            bankAccountId: (activeMethod === 'bank_transfer' || activeMethod === 'card') ? (selectedBankAccountId || undefined) : undefined,
            mobileMoneyProvider: activeMethod === 'mobile_money' ? (momoProvider || 'MoMo') : undefined,
        };

        const updated = [...payments, newPayment];
        setPayments(updated);
        const newTotal = updated.reduce((s, p) => s + (p.amount || 0), 0);
        const newRemaining = Math.max(0, safeTotal - newTotal);
        setAmountInput(newRemaining > 0 ? newRemaining.toFixed(2) : '0');
        setReferenceInput('');
        setMomoProvider('');
        setShowQuickAmounts(false);

        if (newRemaining <= 0.01) {
            setAmountInput('0');
            amountInputRef.current?.blur();
        } else {
            setTimeout(() => amountInputRef.current?.focus(), 100);
        }
    };

    const removePayment = (idx: number): void => {
        const updated = payments.filter((_, i) => i !== idx);
        setPayments(updated);
        const newTotal = updated.reduce((s, p) => s + (p.amount || 0), 0);
        const newRemaining = Math.max(0, safeTotal - newTotal);
        setAmountInput(newRemaining > 0 ? newRemaining.toFixed(2) : '0');
        setTimeout(() => amountInputRef.current?.focus(), 100);
    };

    const resetPayments = (): void => {
        setPayments([]);
        setAmountInput(safeTotal.toFixed(2));
        setReferenceInput('');
        setMomoProvider('');
        amountInputRef.current?.focus();
    };

    const handleCompleteClick = (): void => {
        if (payments.length === 0) {
            setShowConfirmModal(true);
            return;
        }
        finalizePayment();
    };

    const finalizePayment = (): void => {
        let finalPayments = [...payments];

        // Calculate precise remaining amount
        const totalPaid = finalPayments.reduce((s, p) => s + (p.amount || 0), 0);
        let remainingAmount = safeTotal - totalPaid;
        // Round to 2 decimal places to avoid floating point artifacts
        remainingAmount = Math.round(remainingAmount * 100) / 100;

        // Automatically add the remaining balance as cash
        if (remainingAmount > 0) {
            finalPayments.push({
                paymentMethod: 'cash',
                amount: remainingAmount,
                reference: undefined,
                bankAccountId: undefined,
                mobileMoneyProvider: undefined,
            });
        }

        // Ensure all amounts are numbers
        finalPayments = finalPayments.map(p => ({
            ...p,
            amount: typeof p.amount === 'string' ? parseFloat(p.amount) : p.amount,
        }));

        setShowConfirmModal(false);
        onComplete(finalPayments, String(discountTotal || 0));
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (isFullyPaid || totalPaid >= safeTotal) {
                handleCompleteClick();
            } else {
                addPayment();
            }
        }
        paymentMethods.forEach((m, i) => {
            if (e.key === String(i + 1)) {
                setActiveMethod(m.type);
                amountInputRef.current?.focus();
            }
        });
        if (e.key === 'f' || e.key === 'F') {
            e.preventDefault();
            handleCompleteClick();
        }
        if (e.key === 'Escape') {
            if (showConfirmModal) { setShowConfirmModal(false); }
            else if (showQuickAmounts) { setShowQuickAmounts(false); }
            else { onClose(); }
        }
    };

    if (loadingMethods) {
        return (
            <AnimatePresence>
                {isOpen && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3">
                        <motion.div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-8 text-center">
                            <Loader2 size={32} className="animate-spin text-emerald-500 mx-auto mb-3" />
                            <p className="text-slate-500 font-medium">Loading payment methods...</p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3"
                >
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] overflow-hidden flex flex-col"
                        onKeyDown={handleKeyDown}
                    >
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0 bg-white">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-md">
                                    <Wallet size={18} className="text-white" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-slate-900">Payment</h2>
                                    <p className="text-[11px] text-slate-500">
                                        Press 1-{paymentMethods.length} to switch · Enter to add · F to finish
                                    </p>
                                </div>
                            </div>
                            <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 transition-colors" type="button">
                                <X size={18} className="text-slate-400" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
                            {/* Total Display */}
                            <div className={clsx(
                                'text-center p-4 rounded-xl transition-all relative overflow-hidden bg-white shadow-sm',
                                isFullyPaid ? 'ring-2 ring-emerald-300' : isOverpaid ? 'ring-2 ring-rose-300' : 'ring-1 ring-slate-200'
                            )}>
                                {/* Progress bar */}
                                {totalPaid > 0 && !isFullyPaid && (
                                    <div className="absolute bottom-0 left-0 h-1 bg-emerald-500 transition-all duration-300"
                                        style={{ width: `${Math.min(paymentProgress, 100)}%` }}
                                    />
                                )}

                                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                                    Amount to Charge
                                </p>
                                <p className="text-3xl font-black text-slate-900">{formatNaira(safeTotal)}</p>

                                {totalPaid > 0 && (
                                    <div className="mt-2 flex items-center justify-center gap-3 text-sm">
                                        <div className="text-center">
                                            <p className="text-[9px] text-slate-400 uppercase">Paid</p>
                                            <p className="font-bold text-emerald-600">{formatNaira(totalPaid)}</p>
                                        </div>
                                        {!isFullyPaid && (
                                            <>
                                                <ArrowRight size={14} className="text-slate-300" />
                                                <div className="text-center">
                                                    <p className="text-[9px] text-slate-400 uppercase">Remaining</p>
                                                    <p className="font-bold text-amber-600">{formatNaira(remaining)}</p>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Loyalty Points Redemption Banner */}
                            {customerInfo && availablePoints >= 100 && remaining > 0 && (
                                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300/80 rounded-2xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                                            <Star size={16} className="fill-white" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="text-xs font-bold text-amber-950 truncate">
                                                {customerInfo.name} has {availablePoints.toLocaleString()} Loyalty Points
                                            </p>
                                            <p className="text-[11px] text-amber-700 font-medium">
                                                Worth <strong className="font-bold text-amber-900">₦{availableNairaValue.toLocaleString()}</strong> in store purchases
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        disabled={isRedeemingLoyalty}
                                        onClick={handleRedeemLoyalty}
                                        className="px-3.5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                                    >
                                        {isRedeemingLoyalty ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                                        <span>Redeem ₦{Math.min(availableNairaValue, remaining).toLocaleString()}</span>
                                    </button>
                                </div>
                            )}

                            {loyaltyMessage && (
                                <div className="px-3 py-2 bg-amber-100/60 border border-amber-300/60 rounded-xl text-xs font-medium text-amber-900 flex items-center gap-2">
                                    <Sparkles size={14} className="text-amber-600 flex-shrink-0" />
                                    <span>{loyaltyMessage}</span>
                                </div>
                            )}

                            {/* Payment Method Tabs */}
                            <div className={clsx('grid gap-1.5', paymentMethods.length <= 4 ? 'grid-cols-4' : 'grid-cols-3')}>
                                {paymentMethods.map((method) => (
                                    <motion.button
                                        key={method.id}
                                        whileHover={{ scale: 1.03 }}
                                        whileTap={{ scale: 0.97 }}
                                        onClick={() => { setActiveMethod(method.type); amountInputRef.current?.focus(); }}
                                        className={clsx(
                                            'flex flex-col items-center gap-1 p-2.5 rounded-xl text-[10px] font-bold transition-all border-2 relative bg-white',
                                            activeMethod === method.type
                                                ? `${method.bg} ${method.border} ${method.color} shadow-md`
                                                : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                        )}
                                        type="button"
                                    >
                                        <div className={clsx(
                                            'w-9 h-9 rounded-lg flex items-center justify-center transition-all',
                                            activeMethod === method.type
                                                ? `bg-gradient-to-br ${method.gradient} text-white shadow-md`
                                                : 'bg-slate-100'
                                        )}>
                                            <method.icon size={18} className={activeMethod === method.type ? 'text-white' : ''} />
                                        </div>
                                        <span className="truncate w-full text-center leading-tight">{method.name}</span>
                                        <span className={clsx(
                                            'text-[8px] font-mono',
                                            activeMethod === method.type ? 'opacity-70' : 'opacity-40'
                                        )}>
                                            [{method.key}]
                                        </span>

                                        {activeMethod === method.type && (
                                            <motion.div
                                                layoutId="activeMethodIndicator"
                                                className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-current rounded-full"
                                            />
                                        )}
                                    </motion.button>
                                ))}
                            </div>

                            {/* Amount Input */}
                            <div className="bg-white rounded-xl p-3 shadow-sm ring-1 ring-slate-200">
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-400">₦</span>
                                    <input
                                        ref={amountInputRef}
                                        type="number"
                                        value={amountInput}
                                        onChange={(e) => setAmountInput(e.target.value)}
                                        placeholder="0.00"
                                        disabled={isFullyPaid}
                                        className={clsx(
                                            'w-full pl-9 pr-10 py-3.5 border-2 rounded-xl text-2xl font-bold bg-white transition-all text-center',
                                            isFullyPaid
                                                ? 'border-emerald-300 bg-emerald-50/50'
                                                : 'border-slate-200 focus:ring-4 focus:ring-emerald-500/15 focus:border-emerald-400'
                                        )}
                                    />

                                    {!isFullyPaid && (
                                        <button
                                            onClick={() => setShowQuickAmounts(!showQuickAmounts)}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors"
                                            type="button"
                                            title="Quick amounts"
                                        >
                                            <Calculator size={16} />
                                        </button>
                                    )}
                                </div>

                                {/* Quick Amounts */}
                                <AnimatePresence>
                                    {showQuickAmounts && !isFullyPaid && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="overflow-hidden"
                                        >
                                            <div className="flex gap-1.5 mt-2 flex-wrap">
                                                {QUICK_AMOUNTS.map(amount => (
                                                    <motion.button
                                                        key={amount}
                                                        whileHover={{ scale: 1.05 }}
                                                        whileTap={{ scale: 0.95 }}
                                                        onClick={() => addPayment(Math.min(amount, remaining))}
                                                        className={clsx(
                                                            'px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border-2',
                                                            amount <= remaining
                                                                ? 'bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-600'
                                                                : 'bg-slate-50 border-slate-100 text-slate-300 cursor-not-allowed'
                                                        )}
                                                        type="button"
                                                        disabled={amount > remaining}
                                                    >
                                                        {formatNaira(amount)}
                                                    </motion.button>
                                                ))}
                                                <motion.button
                                                    whileHover={{ scale: 1.05 }}
                                                    whileTap={{ scale: 0.95 }}
                                                    onClick={() => addPayment(remaining)}
                                                    className="px-2.5 py-1.5 rounded-lg text-[10px] font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-all"
                                                    type="button"
                                                >
                                                    Exact: {formatNaira(remaining)}
                                                </motion.button>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Reference for bank transfer */}
                            {activeMethod === 'bank_transfer' && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="bg-white rounded-xl p-3 shadow-sm ring-1 ring-slate-200"
                                >
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Transaction Reference
                                    </label>
                                    <div className="flex gap-1.5">
                                        <input
                                            type="text"
                                            value={referenceInput}
                                            onChange={(e) => setReferenceInput(e.target.value)}
                                            placeholder={generateReference()}
                                            className="flex-1 px-3 py-2 border-2 border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500/15 focus:border-emerald-400 transition-all"
                                        />
                                        <button
                                            onClick={() => setReferenceInput(generateReference())}
                                            className="px-3 py-2 bg-slate-100 rounded-lg text-xs font-bold hover:bg-slate-200 transition-all whitespace-nowrap"
                                            type="button"
                                        >
                                            Auto
                                        </button>
                                    </div>
                                </motion.div>
                            )}

                            {/* Bank Account Selection */}
                            {(activeMethod === 'bank_transfer' || activeMethod === 'card') && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="bg-white rounded-xl p-3 shadow-sm ring-1 ring-slate-200"
                                >
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Receiving Account
                                    </label>
                                    {loadingAccounts ? (
                                        <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg text-sm text-slate-400">
                                            <Loader2 size={14} className="animate-spin" /> Loading bank accounts...
                                        </div>
                                    ) : bankAccounts.length === 0 ? (
                                        <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                                            <p className="text-xs text-amber-700 flex items-center gap-1.5 font-medium">
                                                <AlertCircle size={14} className="flex-shrink-0" />
                                                No bank accounts configured. Add bank accounts in Settings &gt; Bank Accounts.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-1.5 max-h-48 overflow-y-auto pr-0.5">
                                            {bankAccounts.map((a: any) => {
                                                const isSelected = selectedBankAccountId === a.id;
                                                return (
                                                    <button
                                                        key={a.id}
                                                        type="button"
                                                        onClick={() => setSelectedBankAccountId(a.id)}
                                                        className={clsx(
                                                            'w-full text-left p-2.5 rounded-xl border-2 transition-all flex items-center justify-between',
                                                            isSelected
                                                                ? 'border-emerald-500 bg-emerald-50/80 text-emerald-950 shadow-xs ring-1 ring-emerald-500/20'
                                                                : 'border-slate-200 hover:border-slate-300 bg-white text-slate-700'
                                                        )}
                                                    >
                                                        <div className="flex items-center gap-2.5 min-w-0">
                                                            <div className={clsx(
                                                                'w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors',
                                                                isSelected ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'
                                                            )}>
                                                                <Building2 size={14} />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="text-xs font-bold truncate">{a.bankName}</p>
                                                                <p className="text-[11px] font-mono text-slate-500 truncate">
                                                                    {a.accountNumber} <span className="font-sans text-slate-400">· {a.accountName}</span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                        {isSelected ? (
                                                            <CheckCircle size={16} className="text-emerald-600 flex-shrink-0 ml-2" />
                                                        ) : (
                                                            <div className="w-4 h-4 rounded-full border border-slate-300 flex-shrink-0 ml-2" />
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {/* Mobile Money Provider */}
                            {activeMethod === 'mobile_money' && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="bg-white rounded-xl p-3 shadow-sm ring-1 ring-slate-200"
                                >
                                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
                                        Provider
                                    </label>
                                    <div className="flex gap-1.5 flex-wrap">
                                        {momoProviders.map((provider: string) => (
                                            <button
                                                key={provider}
                                                onClick={() => setMomoProvider(provider)}
                                                className={clsx(
                                                    'px-2.5 py-1.5 rounded-lg text-[10px] font-bold transition-all border-2',
                                                    momoProvider === provider
                                                        ? 'border-amber-400 bg-amber-50 text-amber-700 shadow-sm'
                                                        : 'border-slate-200 text-slate-500 hover:border-slate-300'
                                                )}
                                                type="button"
                                            >
                                                {provider}
                                            </button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}

                            {/* Add Payment Button */}
                            {!isFullyPaid && (
                                <Button variant="secondary" className="w-full" onClick={() => addPayment()} size="lg">
                                    <Plus size={16} className="mr-1.5" /> Add {activeMethodConfig?.name || 'Payment'}
                                </Button>
                            )}

                            {/* Payments List */}
                            <AnimatePresence>
                                {payments.length > 0 && (
                                    <motion.div
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        className="space-y-1.5"
                                    >
                                        <div className="flex items-center justify-between">
                                            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                                                Payments ({payments.length})
                                            </p>
                                            <button
                                                onClick={resetPayments}
                                                className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-rose-500 font-medium transition-colors"
                                                type="button"
                                            >
                                                <RotateCcw size={10} />
                                                Reset
                                            </button>
                                        </div>
                                        {payments.map((p, i) => {
                                            const method = paymentMethods.find(m => m.type === p.paymentMethod);
                                            const Icon = method?.icon || Banknote;
                                            const colors = METHOD_COLORS[p.paymentMethod] || METHOD_COLORS.other;
                                            return (
                                                <motion.div
                                                    key={i}
                                                    initial={{ opacity: 0, x: 10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: -10 }}
                                                    className="flex items-center gap-2.5 p-2.5 bg-white rounded-lg ring-1 ring-slate-200 hover:ring-slate-300 transition-all group"
                                                >
                                                    <div className={`p-1.5 rounded-lg ${colors.bg}`}>
                                                        <Icon size={14} className={colors.color} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-xs font-bold text-slate-800">
                                                            {p.paymentMethod === 'mobile_money' ? p.mobileMoneyProvider || 'MoMo' : method?.name}
                                                        </p>
                                                        {p.reference && (
                                                            <p className="text-[9px] text-slate-400 truncate font-mono">
                                                                Ref: {p.reference}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-bold text-slate-800 tabular-nums">
                                                        {formatNaira(p.amount)}
                                                    </span>
                                                    <button
                                                        onClick={() => removePayment(i)}
                                                        className="p-1 rounded text-slate-300 hover:text-rose-500 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition-all"
                                                        type="button"
                                                    >
                                                        <Trash2 size={13} />
                                                    </button>
                                                </motion.div>
                                            );
                                        })}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-3 border-t border-slate-100 bg-white flex-shrink-0 space-y-2">
                            {!isFullyPaid && totalPaid > 0 && remaining > 0.99 && !showConfirmModal && (
                                <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                                    <AlertCircle size={14} className="text-amber-500 flex-shrink-0" />
                                    <p className="text-xs text-amber-700">
                                        <strong>{formatNaira(remaining)}</strong> still outstanding.
                                    </p>
                                </div>
                            )}
                            {isOverpaid && (
                                <div className="flex items-center gap-2 p-2.5 bg-rose-50 border border-rose-200 rounded-lg">
                                    <AlertCircle size={14} className="text-rose-500 flex-shrink-0" />
                                    <p className="text-xs text-rose-700">
                                        Overpaid by <strong>{formatNaira(Math.abs(safeTotal - totalPaid))}</strong>. Change due.
                                    </p>
                                </div>
                            )}

                            {showConfirmModal ? (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-3 bg-amber-50 border-2 border-amber-300 rounded-xl"
                                >
                                    <div className="flex items-start gap-2 mb-2">
                                        <ShieldAlert size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-bold text-amber-800">No Payment Method Selected</p>
                                            <p className="text-xs text-amber-600 mt-0.5">
                                                Full amount <strong>{formatNaira(safeTotal)}</strong> will be charged as <strong>Cash</strong>.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setShowConfirmModal(false)}
                                            className="flex-1 px-3 py-2 bg-white border border-amber-300 rounded-lg text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-all"
                                            type="button"
                                        >
                                            Go Back
                                        </button>
                                        <button
                                            onClick={finalizePayment}
                                            className="flex-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 transition-all"
                                            type="button"
                                        >
                                            Yes, Charge as Cash
                                        </button>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.button
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                    onClick={handleCompleteClick}
                                    disabled={isLoading}
                                    className={clsx(
                                        'flex items-center justify-center gap-2 w-full px-6 py-3.5 rounded-xl font-bold text-sm shadow-lg transition-all',
                                        isFullyPaid
                                            ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-emerald-500/25 hover:from-emerald-700 hover:to-teal-700'
                                            : payments.length > 0
                                                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-blue-500/25 hover:from-blue-700 hover:to-indigo-700'
                                                : 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-emerald-500/25 hover:from-emerald-700 hover:to-teal-700',
                                        'disabled:opacity-50 disabled:cursor-not-allowed'
                                    )}
                                    type="button"
                                >
                                    {isLoading ? (
                                        <Loader2 size={16} className="animate-spin" />
                                    ) : (
                                        <>{isFullyPaid ? <CheckCircle size={16} /> : <Receipt size={16} />}</>
                                    )}
                                    {payments.length === 0
                                        ? `Charge ${formatNaira(safeTotal)} as Cash`
                                        : isFullyPaid
                                            ? `Complete Payment · ${formatNaira(totalPaid)}`
                                            : `Complete (${formatNaira(remaining)} remaining)`}
                                </motion.button>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}